import React, { useState } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { ChevronDownIcon, FolderIcon } from '@heroicons/react/24/outline';
import './ProjectSelector.css';

const ProjectSelector: React.FC = () => {
  const { currentProject, availableProjects, switchProject, isLoading } = useProject();
  const [isOpen, setIsOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="project-selector-loading">
        Loading projects...
      </div>
    );
  }

  if (availableProjects.length === 0) {
    return (
      <div className="project-selector">
        <div className="project-badge-empty">
          <FolderIcon className="project-icon" />
          <span>No Projects</span>
        </div>
      </div>
    );
  }

  // Display current selection ("All Projects" if none selected)
  const displayName = currentProject?.alias || 'All Projects';

  return (
    <div className="project-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="project-dropdown-button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select project"
      >
        <div className="project-dropdown-button-content">
          <FolderIcon className="project-icon" />
          <span>{displayName}</span>
        </div>
        <ChevronDownIcon
          className={`project-dropdown-icon ${isOpen ? 'open' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            className="project-dropdown-backdrop"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Dropdown menu */}
          <div
            className="project-dropdown-menu"
            role="listbox"
            aria-label="Available projects"
          >
            {/* "All Projects" option */}
            <button
              onClick={() => {
                switchProject(null);
                setIsOpen(false);
              }}
              className={`project-dropdown-item ${!currentProject ? 'selected' : ''}`}
              role="option"
              aria-selected={!currentProject}
            >
              <FolderIcon className="project-icon" />
              <span className="project-dropdown-item-name">All Projects</span>
              {!currentProject && (
                <span className="project-dropdown-checkmark">✓</span>
              )}
            </button>

            {/* Divider */}
            <div className="project-dropdown-divider" />

            {/* Individual projects */}
            {[...availableProjects]
              .sort((a, b) => a.alias.localeCompare(b.alias, undefined, { sensitivity: 'base' }))
              .map((project) => {
                const isSelected = currentProject?.uuid === project.uuid;

                return (
                  <button
                    key={project.uuid}
                    onClick={() => {
                      switchProject(project.uuid);
                      setIsOpen(false);
                    }}
                    className={`project-dropdown-item ${isSelected ? 'selected' : ''}`}
                    role="option"
                    aria-selected={isSelected}
                    title={project.description || project.alias}
                  >
                    <FolderIcon className="project-icon" />
                    <span className="project-dropdown-item-name">
                      {project.alias}
                    </span>
                    {isSelected && (
                      <span className="project-dropdown-checkmark">✓</span>
                    )}
                  </button>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectSelector;
